import {
  MessageBar,
  MessageBarBody,
  MessageBarGroup,
  MessageBarTitle,
} from "@fluentui/react-components";
import React, { useContext } from "react";

import { NotificationContext } from "../../context/NotificationContext";
import { CommonHelper } from "../../utils/common/CommonHelper";

/**
 * AppNotificationBanner is a functional React component that displays a group of notification messages.
 * It retrieves notifications from the `NotificationContext` and renders each notification as a `MessageBar`.
 * This is mostly used to display Success, error, warning, and info messages to the user.
 *
 * @component
 *
 * @returns {JSX.Element} A group of animated `MessageBar` components, each representing a notification.
 *
 * @dependencies
 * - `NotificationContext`: Provides the list of notifications.
 * - `CommonHelper`: Utility for generating unique keys and determining message bar types.
 */
const AppNotificationBanner: React.FunctionComponent = () => {
  const { notifications } = useContext(NotificationContext);

  return (
    <MessageBarGroup
      animate={"both"}
      className="pt-sm-2"
      data-testid="notification-banner"
    >
      {notifications.map((notification) => (
        <MessageBar
          key={CommonHelper.getGuid()}
          intent={CommonHelper.getMessageBarType(notification.type)}
        >
          <MessageBarBody>
            <MessageBarTitle>{notification.message}</MessageBarTitle>
          </MessageBarBody>
        </MessageBar>
      ))}
    </MessageBarGroup>
  );
};

export default React.memo(AppNotificationBanner);
